import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_VALUES = ["Product Enquiry", "Import Requirement", "Export Requirement", "Sourcing Requirement", "Partnership", "Other"];
const PRODUCT_VALUES = ["Cereals", "Spices", "Pulses", "Agro Commodities", "Nuts", "Dry Fruits", "Other"];

const inputCls =
  "w-full rounded-md border border-amber-500/20 bg-[#0B1D16]/60 px-4 py-3 text-sm text-emerald-50 placeholder:text-slate-500 outline-none transition-colors focus:border-amber-500/60 focus:bg-[#0B1D16]";

const labelCls = "mb-1.5 block text-xs uppercase tracking-[0.18em] text-slate-400 font-display";

const Field = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

export const EnquiryForm = ({ initialProduct = "", initialType = "" }) => {
  const { t } = useLanguage();
  const typeLabels = t("form.types");
  const productLabels = t("form.productOptions");
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    country: "",
    email: "",
    phone: "",
    enquiry_type: initialType,
    product: initialProduct,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
    } catch (err) {
      toast.error(t("form.error"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid="enquiry-form-success"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center py-14 text-center"
      >
        <CheckCircle2 className="h-14 w-14 text-amber-400" strokeWidth={1.4} />
        <h3 className="mt-6 font-serif text-2xl text-emerald-50">{t("form.successTitle")}</h3>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300/90">{t("form.successText")}</p>
        <button
          data-testid="enquiry-form-another-btn"
          onClick={() => {
            setDone(false);
            setForm({
              full_name: "",
              company_name: "",
              country: "",
              email: "",
              phone: "",
              enquiry_type: initialType,
              product: initialProduct,
              message: "",
            });
          }}
          className="mt-8 text-xs uppercase tracking-[0.2em] text-amber-400 underline-offset-4 hover:underline"
        >
          {t("form.another")}
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      <Field label={t("form.name")}>
        <input
          data-testid="enquiry-form-name-input"
          required
          value={form.full_name}
          onChange={set("full_name")}
          className={inputCls}
          placeholder={t("form.namePh")}
        />
      </Field>
      <Field label={t("form.company")}>
        <input
          data-testid="enquiry-form-company-input"
          value={form.company_name}
          onChange={set("company_name")}
          className={inputCls}
          placeholder={t("form.companyPh")}
        />
      </Field>
      <Field label={t("form.country")}>
        <input
          data-testid="enquiry-form-country-input"
          required
          value={form.country}
          onChange={set("country")}
          className={inputCls}
          placeholder={t("form.countryPh")}
        />
      </Field>
      <Field label={t("form.email")}>
        <input
          data-testid="enquiry-form-email-input"
          required
          type="email"
          value={form.email}
          onChange={set("email")}
          className={inputCls}
          placeholder={t("form.emailPh")}
          dir="ltr"
        />
      </Field>
      <Field label={t("form.phone")}>
        <input
          data-testid="enquiry-form-phone-input"
          required
          value={form.phone}
          onChange={set("phone")}
          className={inputCls}
          placeholder={t("form.phonePh")}
          dir="ltr"
        />
      </Field>
      <Field label={t("form.type")}>
        <select
          data-testid="enquiry-form-type-select"
          required
          value={form.enquiry_type}
          onChange={set("enquiry_type")}
          className={inputCls}
        >
          <option value="" disabled>
            {t("form.selectType")}
          </option>
          {TYPE_VALUES.map((v, i) => (
            <option key={v} value={v}>
              {typeLabels[i]}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("form.product")}>
        <select
          data-testid="enquiry-form-product-select"
          value={form.product}
          onChange={set("product")}
          className={inputCls}
        >
          <option value="" disabled>
            {t("form.selectProduct")}
          </option>
          {PRODUCT_VALUES.map((v, i) => (
            <option key={v} value={v}>
              {productLabels[i]}
            </option>
          ))}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <label className={labelCls}>{t("form.message")}</label>
        <textarea
          data-testid="enquiry-form-message-input"
          required
          rows={4}
          value={form.message}
          onChange={set("message")}
          className={`${inputCls} resize-none`}
          placeholder={t("form.messagePh")}
        />
      </div>
      <div className="sm:col-span-2">
        <button
          data-testid="enquiry-form-submit-btn"
          type="submit"
          disabled={loading}
          className="group inline-flex w-full items-center justify-center gap-3 rounded-md bg-gradient-to-br from-[#DFB756] to-[#C49A2D] px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.18em] text-[#0B1D16] transition-transform duration-300 hover:scale-[1.015] disabled:opacity-60 sm:w-auto"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180" />
          )}
          {loading ? t("form.sending") : t("form.submit")}
        </button>
      </div>
    </form>
  );
};
